import React, { useState, useEffect } from 'react';
import axios from 'axios';
import BottomNav from '../../components/BottomNav';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SendGift = () => {
  const navigate = useNavigate();
  const [receiverEmail, setReceiverEmail] = useState('');
  const [amount, setAmount] = useState('');
  const [selectedDenomination, setSelectedDenomination] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [denominations, setDenominations] = useState([]);

  useEffect(() => {
    fetchDenominations();
  }, []);

  const fetchDenominations = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/merchant/denominations`);
      setDenominations(data.denominations || []);
    } catch (error) {
      console.error('Failed to fetch denominations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const finalAmount = selectedDenomination || parseFloat(amount);
    
    if (!finalAmount || finalAmount <= 0) {
      toast.error('Please select or enter a valid amount');
      return;
    }

    setLoading(true);

    try {
      const { data } = await axios.post(
        `${API_URL}/api/consumer/send-gift`,
        {
          receiver_email: receiverEmail,
          amount: finalAmount,
          denomination: selectedDenomination,
          message: message
        },
        { withCredentials: true }
      );

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create gift card');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#18261E] pb-24">
      {/* Header */}
      <div className="bg-[#283D32]/80 backdrop-blur-xl border-b border-[#3E5248] px-6 py-6">
        <h1 className="text-3xl tracking-tight text-white" data-testid="send-gift-title">Send a Gift</h1>
        <p className="text-[#889990] mt-2">Share the love with a universal gift card</p>
      </div>

      <div className="px-6 py-8">
        <form onSubmit={handleSubmit} className="bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-8" data-testid="send-gift-form">
          <div className="space-y-6">
            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Recipient Email
              </label>
              <input
                type="email"
                value={receiverEmail}
                onChange={(e) => setReceiverEmail(e.target.value)}
                required
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365]"
                placeholder="friend@example.com"
                data-testid="receiver-email-input"
              />
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Quick Select Amount
              </label>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {denominations.map((denom) => (
                  <button
                    key={denom}
                    type="button"
                    onClick={() => {
                      setSelectedDenomination(denom);
                      setAmount('');
                    }}
                    className={`px-4 py-3 rounded-xl font-medium transition-all ${
                      selectedDenomination === denom
                        ? 'bg-[#C3A365] text-[#18261E]'
                        : 'bg-[#283D32] text-white border border-[#3E5248] hover:border-gray-500'
                    }`}
                    data-testid={`denomination-${denom}`}
                  >
                    £{denom}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Or Custom Amount (GBP)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C3A365]">£</span>
                <input
                  type="number"
                  step="0.01"
                  min="1"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setSelectedDenomination(null);
                  }}
                  className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl pl-8 pr-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365]"
                  placeholder="25.00"
                  data-testid="amount-input"
                />
              </div>
              {selectedDenomination && (
                <p className="text-xs text-[#E8EBE9] mt-1">Selected: £{selectedDenomination}</p>
              )}
            </div>

            <div>
              <label className="block text-xs tracking-[0.2em] uppercase text-[#889990] font-medium mb-2">
                Personal Message (Optional)
              </label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full bg-[#283D32] border border-[#3E5248] rounded-xl px-4 py-3 text-base text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 outline-none transition-all placeholder:text-[#C3A365] resize-none"
                placeholder="Add a personal touch..."
                data-testid="message-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#C3A365] text-[#18261E] hover:bg-white px-8 py-4 rounded-full font-medium tracking-wide text-base transition-all hover:-translate-y-1 hover:shadow-md active:scale-[0.97] disabled:opacity-50"
              data-testid="send-gift-submit-button"
            >
              {loading ? 'Processing...' : 'Continue to Payment'}
            </button>
          </div>
        </form>

        <div className="mt-8 bg-[#283D32]/50 backdrop-blur-sm border border-[#3E5248] rounded-2xl p-6">
          <h3 className="text-lg font-medium text-white mb-3">How it works</h3>
          <ul className="space-y-2 text-sm text-[#889990]">
            <li>• Recipient gets an email with their gift card</li>
            <li>• Works at any participating habitué merchant</li>
            <li>• Valid for 24 months from purchase</li>
            <li>• Earn 1 habitué point per £1 spent</li>
            <li>• Secure payment via Stripe</li>
          </ul>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default SendGift;
